import { KalturaAssetType } from '@kaltura-ott/ott-client-types';
import { KalturaAsset } from '@kaltura-ott/ott-client-types/api/types';
import {
  findRailImageV2,
  findRailImageV3,
  generateMenuItem,
  getRatioByType,
  getWidthByHeight,
  KuxFilteredCategoriesDTO,
  KuxImageRail,
  KuxMediaAssetType,
  KuxMenuItem,
  RatingProviderEnum,
  UIDAPresentationTypeEnum,
  KuxRailImageArg,
  PresentationAdditionalTypeEnum,
  KuxCardPlaceholderType,
  getMediaAssetsByAssetHistory,
  assetToType,
  KuxErrorType,
} from '@kaltura-ott/tvpil';
import { createErrorEvent, createRailEvent, tvpilJournal } from '@kaltura-ott/tvpil-utils';
import { action, observable, computed } from 'mobx';
import { lastValueFrom } from 'rxjs';
import { container } from 'tsyringe';

import { featureEnabler, RatingInfo, ratingMetaKeys } from '../../../../utils';
import { errorHandling } from '../../../../utils/error-handling';
import type { AppStore } from '../../../app/app-store';
import { CardPlaceholder } from '../../../entities/CardPlaceholder';
import { creatorEntityStore } from '../../../entities/creator.entity';
import { EntitiesType } from '../../../entities/entities.types';
import { MediaAsset } from '../../../entities/MediaAsset';
import { ErrorPriority } from '../../../error';
import { TopShelfStore } from '../../../topShelf/top-shelf-store';
import { CategoryChildren } from '../../category-children';
import { DataRailV2, ItemImageOptions, RailImageType } from '../data-rail-v2';
import { WidgetStore } from '../widget-store';

import { isNewSubtypeSupported } from './rail-store-config';
import { getItemsInPage, getPageIndexByIndex, DEFAULT_PAGE_SIZE } from './rail-store-helper-v2';

// number of items from end to start of upload
const PADDING_SIZE = 2;

export enum RailState {
  IDLE = 'IDLE',
  PENDING = 'PENDING',
  ERROR = 'ERROR',
  SUCCESS = 'SUCCESS',
}

export abstract class RailStoreV2<A extends MediaAsset = MediaAsset> extends WidgetStore implements DataRailV2 {
  static defaultSizeIndex = 1;
  static moduleName = 'RailStoreV2';

  abstract subtype: UIDAPresentationTypeEnum | PresentationAdditionalTypeEnum;
  @observable protected _isReady = false;
  /**
   * Used to store raw rail items
   */
  @observable protected innerItems: (A | CardPlaceholder)[] = [];

  /**
   * Exposes items to clients. Can include additional custom processing.
   */
  @computed public get items() {
    return this.innerItems;
  }

  @computed public get isReady(): boolean {
    return this._isReady;
  }

  @observable totalItemsCount = 0;
  @observable isLoadingState = false;
  @observable isTopShelf = false;

  @observable public _lastPageWasLoaded = false;
  @observable protected _state: RailState = RailState.IDLE;
  protected _error?: KuxErrorType;

  public presentEntitlementIndicator = false;
  /**
          Will be used as a boundary for displaying the Expiration Badges on the card,
          The expiration badge will be displayed only if expiration time was equale \ lower then this field value,
          Will be measured in days.
         */
  public recordingExpirationBoundary?: number = undefined;
  public newEpisodeBadgeDays?: number = undefined;
  public pageReferenceId: string;

  public presentRating = false;
  public presentRatingSourceLogo = false;
  public hideTitles = false;

  /**
   * @deprecated
   */
  public ratingProvider?: RatingProviderEnum;

  public showMoreItem?: KuxMenuItem;

  /**
   * @deprecated
   */
  public railHeight: number;

  public imageHeight: number;
  public imageHeightCompact: number;
  public imageHeightMedium: number;

  public itemDefaultWidth: number;

  public groupBy: string;

  protected opcItemsLimit: number;
  private readonly opcPresentShowMore: boolean;

  protected _removeInvisible = true;

  public readonly defaultChannel?: number;
  public isInitializing = false;
  // used to prevent additional items loading if no more items on server
  @observable protected variableItemsLimit: number;
  private lastLoadedDataPageIndex = 0;
  protected visibleItemsIndexes = new Set<number>();
  private loadedAdditionalDataPageIndexes = new Set<number>();
  private loadedDataPageIndexes = new Set<number>();
  private railImageProperty: KuxFilteredCategoriesDTO['railImageProperty'];
  private topShelfStore?: TopShelfStore;
  private pageIndexesForPlaceholders = new Set<number>();
  private imageHeights: Record<RailImageType, number | undefined>;

  constructor(
    categoryTree: KuxFilteredCategoriesDTO,
    parentCategoryTree: KuxFilteredCategoriesDTO,
    children?: CategoryChildren[],
  ) {
    super(categoryTree, parentCategoryTree, children);
    this.children = children || [];
    if (categoryTree.link?.typeOfAppLink) {
      const { subtype } = this.categoryTree;
      this.showMoreItem = generateMenuItem({
        typeOfAppLink: categoryTree.link.typeOfAppLink,
        inAppLink: (categoryTree.link.targetGridCategoryId || categoryTree.link.inAppLink) as string,
        id: categoryTree.id,
        referenceId: categoryTree.referenceId,
        presentationType: subtype && isNewSubtypeSupported(subtype) ? subtype : undefined,
      });
    }

    // base rail items limit that comes from opc settings
    this.opcItemsLimit = categoryTree.railProperty?.railItemsLimit || Number.MAX_SAFE_INTEGER;
    this.opcPresentShowMore = categoryTree.railProperty?.presentShowMore || false;
    this.variableItemsLimit = this.opcItemsLimit;
    this.groupBy = categoryTree.groupBy || '';
    this.railImageProperty = categoryTree.railImageProperty;
    // TODO deprecated
    this.railHeight = categoryTree.railImageProperty.assetImageHeight
      ? categoryTree.railImageProperty.assetImageHeight * RailStoreV2.defaultSizeIndex
      : 0;

    this.imageHeight = categoryTree.imageScreens.imageHeight ?? 0;
    this.imageHeightCompact = categoryTree.imageScreens.imageHeightCompact ?? 0;
    this.imageHeightMedium = categoryTree.imageScreens.imageHeightMedium ?? 0;

    this.imageHeights = {
      [RailImageType.Default]: this.imageHeight,
      [RailImageType.Compact]: this.imageHeightCompact,
      [RailImageType.Medium]: this.imageHeightMedium,
    };

    this.isTopShelf = categoryTree.railProperty.showTopShelfRailUi || false;
    this.recordingExpirationBoundary = categoryTree.railProperty.recordingExpirationBoundary;
    this.newEpisodeBadgeDays = categoryTree.railProperty.newEpisodeBadgeDays;
    if (categoryTree.railImageProperty.assetPresentationImageType) {
      this.itemDefaultWidth = getWidthByHeight(
        this.railHeight,
        getRatioByType(categoryTree.railImageProperty.assetPresentationImageType),
      );
    } else {
      this.itemDefaultWidth = getWidthByHeight(this.railHeight, categoryTree.railImageProperty.assetPresentationRatio);
    }
    this.ratingProvider =
      categoryTree.railProperty.ratingProvider && RatingProviderEnum[categoryTree.railProperty.ratingProvider]
        ? categoryTree.railProperty.ratingProvider
        : undefined;
    this.presentEntitlementIndicator = categoryTree.railProperty.presentEntitlementIndicator;
    this.presentRatingSourceLogo = categoryTree.railProperty.presentRatingSourceLogo;
    this.presentRating = categoryTree.railProperty.presentRating;
    this.defaultChannel = categoryTree.railProperty.defaultChannel;
    this.parentalPinProtected = categoryTree.parentalPinProtected;
    const appStore = container.resolve<AppStore>('appStore');
    this.topShelfStore = appStore.topShelfStore;
    this.hideTitles = categoryTree.railProperty.hideTitles;
    this.pageReferenceId = parentCategoryTree.referenceId;
  }

  protected errorHandlerCallback(error?: KuxErrorType) {
    this.isLoadingState = false;
    this._isReady = true;

    this._state = RailState.ERROR;
    this._error = error;
    this.isInitializing = false;
    this.totalItemsCount = 0;
  }

  protected get removeInvisible(): boolean {
    if (featureEnabler.variation('NUMBER_OF_SAVED_PAGES_IN_RAIL', 0)) {
      return this._removeInvisible;
    }
    return false;
  }

  protected get lastPageWasLoaded(): boolean {
    return this._lastPageWasLoaded;
  }

  private getAssetImageHeight(options?: ItemImageOptions) {
    if (!options) {
      return this.railImageProperty.assetImageHeight;
    }

    return this.imageHeights[options.imageType] ?? options.defaultHeight;
  }

  public get presentFavoriteIndicator() {
    const appStore = container.resolve<AppStore>('appStore');

    return (
      appStore.rootStore.isFavoriteChannelPersonalization && this.categoryTree.railProperty.presentFavoriteIndicator
    );
  }

  itemImage<T extends KuxMediaAssetType>(
    assetData: T | KuxCardPlaceholderType,
    options?: ItemImageOptions,
  ): KuxImageRail {
    return computed(() => {
      if (!('metas' in assetData)) {
        return {
          height: this.railHeight,
          width: this.itemDefaultWidth,
        };
      }

      const height = this.getAssetImageHeight(options);

      if (this.railImageProperty.assetPresentationImageType) {
        return findRailImageV3(assetData, {
          ...this.railImageProperty,
          assetImageHeight: height,
          sizeIndex: RailStoreV2.defaultSizeIndex,
        } as KuxRailImageArg);
      } else {
        return findRailImageV2(assetData, {
          ...this.railImageProperty,
          assetImageHeight: height,
          sizeIndex: RailStoreV2.defaultSizeIndex,
        } as KuxRailImageArg);
      }
    }).get();
  }

  // determines if rail can load more items
  // can be less then opc limit, so moved to separate computed
  @computed get fullyLoaded() {
    return this.variableItemsLimit === this.innerItems.length;
  }

  // defines if rails should show "more" card
  @computed get presentShowMore() {
    return (
      // is show more enabled in opc
      !!this.opcPresentShowMore &&
      !!this.showMoreItem &&
      // don't show button unless limit is reached
      this.isOpcItemsLimitReached &&
      // show only if we have more items than are allowed to display
      this.totalItemsCount > this.opcItemsLimit
    );
  }

  @computed get isRailEmpty() {
    return this.isReady && !this.items.length;
  }
  // determine if rail can't load more items due to opc limitations
  // returns false when limit is falsy (e.g. 0)
  @computed protected get isOpcItemsLimitReached() {
    return !!this.opcItemsLimit && this.opcItemsLimit <= this.items.length;
  }

  @computed get state(): RailState {
    return this._state;
  }

  get error(): KuxErrorType {
    // @ts-expect-error TODO Fix
    return this._error;
  }

  @computed get isIdle() {
    return this.state === RailState.IDLE;
  }

  @computed get isLoading() {
    return this.state === RailState.PENDING;
  }

  @computed get isError() {
    return this.state === RailState.ERROR;
  }

  @computed get isSuccess() {
    return this.state === RailState.SUCCESS;
  }

  @action
  @errorHandling({
    type: ErrorPriority.low,
    callback: function (this: RailStoreV2, error) {
      tvpilJournal.log(
        createErrorEvent('RailInitFailed', {
          id: this.categoryTree.id,
          name: this.categoryTree.name,
        }),
      );

      this.errorHandlerCallback(error);
    },
  })
  async init() {
    if (this.isInitializing) {
      // ignore duplicate init
      return;
    }

    if (!this.isInitRequired()) {
      return;
    }

    tvpilJournal.log(
      createRailEvent('InitStart', {
        id: this.categoryTree.id,
        name: this.categoryTree.name,
        subtype: this.categoryTree.subtype,
        type: this.categoryTree.type,
      }),
    );

    this.resetStore();
    this.isInitializing = true;
    this._state = RailState.PENDING;

    await this.loadWithAdditionalData(1);

    tvpilJournal.log(
      createRailEvent('InitEnd', {
        id: this.categoryTree.id,
        name: this.categoryTree.name,
        subtype: this.categoryTree.subtype,
        type: this.categoryTree.type,
      }),
    );

    this._isReady = true;
    this.isInitializing = false;
  }

  @action
  onInvisible(): void {
    this.expireAdditionData();
  }

  @action
  resetStore() {
    this._state = RailState.IDLE;
    this._error = undefined;
    this.innerItems = [];
    this._lastPageWasLoaded = false;
    this.lastLoadedDataPageIndex = 0;
    this.variableItemsLimit = this.opcItemsLimit;
    this.loadedDataPageIndexes = new Set();
    this.isLoadingState = false;
    this.expireAdditionData();
    this._isReady = false;
    this.isInitializing = false;
  }

  @errorHandling({ type: ErrorPriority.low })
  public setVisibleIndex(index: number): void {
    this.visibleItemsIndexes.add(index);
    this.recalculateVisiblePageIndex();
  }

  public setInvisibleIndex(index: number, deferred = false): void {
    this.visibleItemsIndexes.delete(index);
    if (this.removeInvisible) {
      const pageIndexLeft = getPageIndexByIndex(index) - featureEnabler.variation('NUMBER_OF_SAVED_PAGES_IN_RAIL', 0);
      const pageIndexRight = getPageIndexByIndex(index) + featureEnabler.variation('NUMBER_OF_SAVED_PAGES_IN_RAIL', 0);

      if (deferred) {
        this.pageIndexesForPlaceholders.add(pageIndexLeft);
        this.pageIndexesForPlaceholders.add(pageIndexRight);

        if (this.visibleItemsIndexes.size === 0) {
          setTimeout(() => {
            this.createPlaceHolders(Array.from(this.pageIndexesForPlaceholders), true);
            this.pageIndexesForPlaceholders.clear();
          }, 0);
        }
      } else {
        this.createPlaceHolders([pageIndexLeft]);
        this.createPlaceHolders([pageIndexRight]);
      }
    }
  }

  /**
   * @deprecated
   * see Entity.rating && this.presentRating && this.presentRatingSourceLogo
   */
  public getRating(card: A): RatingInfo | null {
    if (!this.ratingProvider || !this.presentRating) {
      return null;
    }
    const rating = card.data.metas[ratingMetaKeys[this.ratingProvider]];
    const stars = rating ? String(rating) : undefined;
    if (!stars) {
      return null;
    }
    return {
      ratingProvider: this.ratingProvider,
      presentRatingSourceLogo: this.presentRatingSourceLogo,
      stars,
    };
  }

  public async loadDataPaginated(pagesAmount?: number) {
    let pageForDownload = 1;
    while ((typeof pagesAmount === 'number' ? pagesAmount-- : true) && (await this.loadData(pageForDownload++)));
  }

  public async setFocusedItem(asset: EntitiesType) {
    if (!this.isTopShelf) {
      return;
    }
    await this.topShelfStore?.setAsset(asset);
  }

  // determines page size for next items request
  protected get pageSize(): number {
    return Math.min(DEFAULT_PAGE_SIZE, Math.max(this.variableItemsLimit - this.innerItems.length, 0));
  }

  // call when the total number of elements is known
  protected setNewItemsLimit(totalItemsCount: number) {
    this.totalItemsCount = totalItemsCount;
    if (this.variableItemsLimit >= totalItemsCount) {
      this.variableItemsLimit = totalItemsCount;
    }
  }

  protected isInitRequired(): boolean {
    return !this._isReady;
  }

  private createPlaceHolders(pageIndexes: number[], deferred = false) {
    outer: for (const pageIndex of pageIndexes) {
      if (!deferred) {
        for (let i = (pageIndex - 1) * DEFAULT_PAGE_SIZE; i < pageIndex * DEFAULT_PAGE_SIZE; i++) {
          if (this.visibleItemsIndexes.has(i)) {
            continue outer;
          }
        }
      }

      this.loadedDataPageIndexes.delete(pageIndex);
      for (let i = (pageIndex - 1) * DEFAULT_PAGE_SIZE; i < pageIndex * DEFAULT_PAGE_SIZE; i++) {
        if (this.innerItems[i] && 'mediaType' in this.innerItems[i].data) {
          this.innerItems[i] = new CardPlaceholder({
            // @ts-expect-error TODO Fix types
            assetType: this.innerItems[i].data.mediaType === 'Program' ? KalturaAssetType.epg : KalturaAssetType.media,
            assetId: this.innerItems[i].id,
            isRecordProgram:
              // @ts-expect-error TODO Fix types
              'isRecordProgram' in this.innerItems[i].data ? this.innerItems[i].data.isRecordProgram : false,
          });
        }
      }
    }
  }

  private expireAdditionData() {
    this.loadedAdditionalDataPageIndexes = new Set();
  }

  private async recalculateVisiblePageIndex() {
    const maxIndex = Math.max(...Array.from(this.visibleItemsIndexes.values()));
    const minIndex = Math.min(...Array.from(this.visibleItemsIndexes.values()));

    const nextMainDataPageIndex = getPageIndexByIndex(maxIndex + PADDING_SIZE);
    if (nextMainDataPageIndex > this.lastLoadedDataPageIndex) {
      this.loadWithAdditionalData(nextMainDataPageIndex);
    } else {
      const maxVisiblePageIndex = getPageIndexByIndex(maxIndex);
      const minVisiblePageIndex = getPageIndexByIndex(minIndex);

      this.loadAdditionalData(minVisiblePageIndex);
      this.loadAdditionalData(maxVisiblePageIndex);

      this.restoreData(maxVisiblePageIndex);
      this.restoreData(minVisiblePageIndex);
    }
  }

  private canLoadData(index: number): boolean {
    return !this.fullyLoaded && this.lastLoadedDataPageIndex < index && !!this.pageSize;
  }

  private async loadWithAdditionalData(index: number): Promise<void> {
    const newItems = await this.loadData(index);

    if (newItems) {
      tvpilJournal.log(
        createRailEvent('AdditionalDataLoadingStart', {
          id: this.categoryTree.id,
          name: this.categoryTree.name,
          subtype: this.categoryTree.subtype,
          type: this.categoryTree.type,
        }),
      );

      this.getAdditionData(newItems).then((result) => {
        tvpilJournal.log(
          createRailEvent('AdditionalDataLoadingEnd', {
            id: this.categoryTree.id,
            name: this.categoryTree.name,
            subtype: this.categoryTree.subtype,
            type: this.categoryTree.type,
          }),
        );

        return result;
      });
    }
  }

  canLoadAdditionalData(index: number): boolean {
    return !this.loadedAdditionalDataPageIndexes.has(index);
  }

  @errorHandling({
    type: ErrorPriority.low,
    callback: function (this: RailStoreV2, error) {
      tvpilJournal.log(
        createErrorEvent('RailLoadFailed', {
          id: this.categoryTree.id,
          name: this.categoryTree.name,
        }),
      );

      this.errorHandlerCallback(error);
    },
  })
  protected async loadData(pageIndex: number, minLoadedItems?: number): Promise<undefined | A[]> {
    if (!this.canLoadData(pageIndex)) {
      return;
    }

    const prevLastLoadedDataPageIndex = this.lastLoadedDataPageIndex;
    this.lastLoadedDataPageIndex = pageIndex;
    this.loadedDataPageIndexes.add(pageIndex);
    this.isLoadingState = true;

    try {
      const { totalCount, newItems } = await this.getData(pageIndex, this.innerItems as A[]);
      this.isLoadingState = false;

      this._lastPageWasLoaded = pageIndex * DEFAULT_PAGE_SIZE >= totalCount || totalCount === 0;

      this.setNewItemsLimit(totalCount);
      if (
        totalCount &&
        pageIndex * DEFAULT_PAGE_SIZE < totalCount &&
        newItems.length < (minLoadedItems ? minLoadedItems : DEFAULT_PAGE_SIZE)
      ) {
        // call until there is a full rail
        this.setNewItemsLimit(totalCount);
        return newItems.concat(
          (await this.loadData(pageIndex + 1, (minLoadedItems || DEFAULT_PAGE_SIZE) - newItems.length)) || [],
        );
      }

      this._state = RailState.SUCCESS;

      return newItems;
    } catch (e) {
      this.lastLoadedDataPageIndex = prevLastLoadedDataPageIndex;
      this.loadedDataPageIndexes.delete(pageIndex);

      throw e;
    }
  }

  @errorHandling({
    callback: function (this: RailStoreV2, error) {
      this.errorHandlerCallback(error);
    },
  })
  protected async loadAdditionalData(pageIndex: number) {
    if (!this.canLoadAdditionalData(pageIndex)) {
      return false;
    }
    this.loadedAdditionalDataPageIndexes.add(pageIndex);

    await this.getAdditionData(getItemsInPage(this.innerItems as A[], pageIndex));

    return true;
  }

  protected async restoreData(pageIndex: number) {
    if (this.loadedDataPageIndexes.has(pageIndex) || !this.removeInvisible) {
      return false;
    }
    this.loadedDataPageIndexes.add(pageIndex);
    const itemsInPage = getItemsInPage(this.items, pageIndex);
    const restoreDataItems = itemsInPage.filter((item) => item instanceof CardPlaceholder) as CardPlaceholder[];
    const restoreData = restoreDataItems.map((item) => item.data);

    if (restoreData.length) {
      const restoreItems = (
        await lastValueFrom(getMediaAssetsByAssetHistory(restoreData, this.getRestoreDataKsql()))
      ).objects
        .map((item: KalturaAsset) =>
          assetToType(item, {
            isGroupedGrid: this.categoryTree.railProperty.isGroupedGrid,
            newEpisodeBadgeDays: this.newEpisodeBadgeDays,
            recordingExpirationBoundary: this.recordingExpirationBoundary,
          }),
        )
        .map((item: KuxMediaAssetType | undefined) => (item ? creatorEntityStore(item) : null)) as (A | null)[];
      const newInnerItems = [...this.innerItems];

      for (let i = (pageIndex - 1) * DEFAULT_PAGE_SIZE; i < pageIndex * DEFAULT_PAGE_SIZE; i++) {
        if (this.innerItems[i]) {
          const restoreItem = restoreItems.find((item) => item?.id === this.innerItems[i].id);
          if (restoreItem) {
            newInnerItems[i] = restoreItem;
          }
        }
      }

      this.innerItems = newInnerItems;
      await this.restoreAdditionData(pageIndex, this.items);
    }
  }

  protected abstract getData(
    pageIndex: number,
    items: (A | CardPlaceholder)[],
  ): Promise<{ items: (A | CardPlaceholder)[]; totalCount: number; newItems: A[] }>;

  protected getRestoreDataKsql(): undefined | string {
    return undefined;
  }

  protected abstract getAdditionData(itemsInPage: (A | CardPlaceholder)[]): Promise<void>;
  protected abstract restoreAdditionData(pageIndex: number, items: (A | CardPlaceholder)[]): Promise<void>;
}
